import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiMenu, FiLogOut, FiGlobe, FiCpu, FiShield, FiFileText, FiHome } from 'react-icons/fi';
import StravaConnectButton from '../Auth/StravaConnectButton';
import { useAuth } from '../Auth/AuthContext';

function MenuButton({ label, icon, children, onClick, className = '' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-indigo-600 transition-colors flex items-center gap-3 group ${className}`}
    >
      {icon && <span className="text-gray-400 group-hover:text-indigo-500 transition-colors">{icon}</span>}
      <div className="flex-1">
        <div className="font-semibold">{label}</div>
        {children ? <div className="text-xs text-gray-500 mt-0.5 group-hover:text-indigo-400">{children}</div> : null}
      </div>
    </button>
  );
}

export default function TopRightMenu({ isLoggedIn, displayName, onLogout, onChangeLanguage, currentLanguage }) {
  const { t } = useTranslation();
  const { isRoot, impersonation, searchUsers, impersonate, resetImpersonation } = useAuth();
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const [adminQuery, setAdminQuery] = useState('');
  const [adminResults, setAdminResults] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminError, setAdminError] = useState(null);

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!open) return;
      if (!containerRef.current) return;
      if (containerRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const triggerLabel = isLoggedIn ? (displayName || 'Account') : null;
  const initials = triggerLabel ? triggerLabel.charAt(0).toUpperCase() : '?';

  useEffect(() => {
    let cancelled = false;
    const q = String(adminQuery || '').trim();
    if (!open || !isRoot) return;
    if (!q || q.length < 2) {
      setAdminResults([]);
      setAdminError(null);
      return;
    }
    setAdminLoading(true);
    setAdminError(null);
    searchUsers(q)
      .then((users) => {
        if (cancelled) return;
        setAdminResults(Array.isArray(users) ? users : []);
      })
      .catch((e) => {
        if (cancelled) return;
        setAdminError(String(e?.message || 'Search failed'));
        setAdminResults([]);
      })
      .finally(() => {
        if (cancelled) return;
        setAdminLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [adminQuery, open, isRoot, searchUsers]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full border transition-all duration-200 ${
          open
            ? 'border-indigo-200 bg-indigo-50 text-indigo-700 shadow-inner'
            : 'border-gray-200 bg-white hover:border-gray-300 text-gray-700 shadow-sm hover:shadow'
        }`}
        aria-haspopup="menu"
        aria-expanded={open ? 'true' : 'false'}
      >
        {isLoggedIn ? (
          <>
            <div className="h-7 w-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold shadow-sm">
              {initials}
            </div>
            <span className="max-w-[120px] truncate text-sm font-bold tracking-tight hidden sm:block">{triggerLabel}</span>
            <FiMenu className="w-4 h-4 ml-1 opacity-50" />
          </>
        ) : (
          <div className="p-1">
             <FiMenu className="w-5 h-5" />
          </div>
        )}
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 mt-3 w-80 rounded-2xl border border-gray-100 bg-white backdrop-blur-xl shadow-2xl shadow-gray-200/50 overflow-hidden z-50 ring-1 ring-black/5 origin-top-right transition-all duration-200 animate-in fade-in zoom-in-95"
        >
          <div className="px-4 py-3 bg-gray-50/50 border-b border-gray-100">
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Menu</div>
          </div>

          <div className="py-2">
            <Link
              to="/"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-indigo-600 transition-colors font-semibold group"
            >
              <span className="text-gray-400 group-hover:text-indigo-500 transition-colors"><FiHome /></span>
              {t('nav.home')}
            </Link>

            {isRoot ? (
              <Link
                to="/admin"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-indigo-700 bg-indigo-50/50 hover:bg-indigo-50 font-bold group"
              >
                <span className="text-indigo-400 group-hover:text-indigo-600"><FiShield /></span>
                {t('admin.activity.menu')}
              </Link>
            ) : null}

            <div className="my-2 border-t border-gray-100/60 mx-4"></div>

            <div className="px-4 pb-2 pt-1 text-[10px] font-bold uppercase tracking-wider text-gray-400">
              Legal
            </div>
            <Link
              to="/terms"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-indigo-600 transition-colors font-medium"
            >
              <FiFileText className="w-4 h-4 opacity-50" />
              {t('legal.pages.cgu')}
            </Link>
            <Link
              to="/terms/sales"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-indigo-600 transition-colors font-medium"
            >
              <FiFileText className="w-4 h-4 opacity-50" />
              {t('legal.pages.cgv')}
            </Link>
            <Link
              to="/privacy"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-indigo-600 transition-colors font-medium"
            >
              <FiFileText className="w-4 h-4 opacity-50" />
              {t('legal.pages.privacy')}
            </Link>
            <Link
              to="/ai-data-usage"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-indigo-600 transition-colors font-medium"
            >
              <FiFileText className="w-4 h-4 opacity-50" />
              {t('legal.pages.ai_data_usage')}
            </Link>

            {isLoggedIn && isRoot ? (
              <div className="border-t border-gray-100 pt-3 mt-2 bg-gray-50/30 pb-2">
                <div className="px-4 pb-2 text-[10px] font-bold uppercase tracking-wider text-gray-400">
                  Admin
                </div>

                {impersonation?.active ? (
                  <div className="px-4 pb-3">
                    <div className="text-xs font-bold text-orange-800 bg-orange-50 border border-orange-200 rounded-lg p-3 shadow-sm">
                      <div className="mb-1">Impersonating:</div>
                      <div className="font-extrabold truncate mb-2 text-orange-900">
                        {impersonation?.target_user?.displayName || impersonation?.target_user?.email || impersonation?.target_user?.id}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          resetImpersonation()
                            .then(() => setOpen(false))
                            .catch(() => {});
                        }}
                        className="w-full px-2 py-1.5 rounded-md bg-white border border-orange-200 text-orange-700 hover:bg-orange-100 text-[10px] font-bold uppercase tracking-wide transition-colors"
                      >
                        Reset Identity
                      </button>
                    </div>
                  </div>
                ) : null}

                <div className="px-4 pb-2">
                  <input
                    value={adminQuery}
                    onChange={(e) => setAdminQuery(e.target.value)}
                    placeholder="Search user..."
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 transition-all"
                  />
                  {adminLoading ? <div className="mt-2 text-xs text-gray-500 animate-pulse">Searching…</div> : null}
                  {adminError ? <div className="mt-2 text-xs text-red-600 font-medium">{adminError}</div> : null}
                  {adminResults.length > 0 ? (
                    <div className="mt-2 border border-gray-200 rounded-xl overflow-hidden shadow-sm bg-white">
                      {adminResults.map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => {
                            impersonate(u.id)
                              .then(() => setOpen(false))
                              .catch(() => {});
                          }}
                          className="w-full text-left px-3 py-2.5 hover:bg-indigo-50 transition-colors border-b last:border-0 border-gray-50"
                          title={u.id}
                        >
                          <div className="text-xs font-bold text-gray-900 truncate">
                            {u.displayName || u.email || u.id}
                          </div>
                          <div className="text-[10px] text-gray-500 font-mono truncate opacity-70">
                            {u.email ? u.email : `id: ${u.id}`}
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            <div className="my-2 border-t border-gray-100/60 mx-4"></div>

            <div className="px-4 pb-2 text-[10px] font-bold uppercase tracking-wider text-gray-400 flex items-center gap-2">
              <FiGlobe /> Language
            </div>

            <div className="px-4 pb-3 flex gap-2">
              <button
                type="button"
                onClick={() => {
                  onChangeLanguage('en');
                  setOpen(false);
                }}
                className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  currentLanguage === 'en'
                    ? 'bg-indigo-600 text-white shadow-md shadow-indigo-500/20'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                English
              </button>
              <button
                type="button"
                onClick={() => {
                  onChangeLanguage('fr');
                  setOpen(false);
                }}
                className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  currentLanguage === 'fr'
                    ? 'bg-indigo-600 text-white shadow-md shadow-indigo-500/20'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Français
              </button>
            </div>

            {!isLoggedIn ? (
              <div className="px-4 pb-4 pt-2">
                <StravaConnectButton
                  className="w-full bg-[#FC4C02] text-white font-bold py-2.5 px-4 rounded-xl shadow-lg shadow-orange-500/20 hover:shadow-orange-500/30 hover:bg-[#E34402] transition-all text-sm flex justify-center items-center"
                />
              </div>
            ) : (
              <div className="px-2 pb-2">
                <MenuButton
                  label="Logout"
                  icon={<FiLogOut />}
                  onClick={() => {
                    onLogout();
                    setOpen(false);
                  }}
                  className="text-red-600 hover:bg-red-50 hover:text-red-700 rounded-lg"
                />
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}


